/**
 * Charmify — Product Configurator
 *
 * Features:
 *  - Variant selection via swatches and option buttons
 *  - URL update when variant changes
 *  - Price and availability update
 *  - Add to cart AJAX submission
 *  - Line item properties handling (engraving, memory content, gift lock)
 *  - Form validation
 *  - Loading state on add to cart button
 *  - Success feedback (toast notification)
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Constants                                                           */
  /* ------------------------------------------------------------------ */

  var TOAST_DURATION = 3000; // ms
  var CART_ENDPOINT = '/cart/add.js';

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  var productData = null;
  var currentVariant = null;
  var sectionId = '';

  /* ------------------------------------------------------------------ */
  /* DOM references                                                      */
  /* ------------------------------------------------------------------ */

  var section = null;
  var form = null;
  var variantIdInput = null;
  var addToCartBtn = null;
  var addToCartText = null;
  var priceWrapper = null;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function qs(selector, parent) {
    return (parent || document).querySelector(selector);
  }

  function qsa(selector, parent) {
    return Array.prototype.slice.call((parent || document).querySelectorAll(selector));
  }

  function formatMoney(cents) {
    // Use global Charmify formatMoney if available (from theme.js)
    if (window.Charmify && typeof window.Charmify.formatMoney === 'function') {
      return window.Charmify.formatMoney(cents);
    }
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
    }
    // Fallback: simple dollar format
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ */
  /* Variant matching                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Collect selected option values from radio / select inputs.
   * Returns an array like ['Gold', 'Small'].
   */
  function getSelectedOptions() {
    var options = [];
    var optionFields = qsa('.product-main__option', section);

    optionFields.forEach(function (fieldset) {
      // Try radio first (swatches / pills)
      var checkedRadio = qs('input[type="radio"]:checked', fieldset);
      if (checkedRadio) {
        options.push(checkedRadio.value);
        return;
      }
      // Fallback: select dropdown
      var select = qs('select', fieldset);
      if (select) {
        options.push(select.value);
      }
    });

    return options;
  }

  /**
   * Find the variant object whose option values match the selected array.
   */
  function findVariantFromOptions(selectedOptions) {
    if (!productData || !productData.variants) return null;

    return productData.variants.find(function (variant) {
      return selectedOptions.every(function (value, index) {
        return variant.options[index] === value;
      });
    }) || null;
  }

  /* ------------------------------------------------------------------ */
  /* URL management                                                      */
  /* ------------------------------------------------------------------ */

  function updateUrl(variantId) {
    if (!variantId) return;
    if (!window.history || !window.history.replaceState) return;

    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ path: url.href }, '', url.href);
  }

  /* ------------------------------------------------------------------ */
  /* Price & availability update                                         */
  /* ------------------------------------------------------------------ */

  function updatePriceDisplay(variant) {
    if (!priceWrapper) return;

    // Find or build price elements inside the price wrapper
    // We rely on the rendered `price` snippet, but update text content
    var priceEl = qs('[data-product-price]', priceWrapper) || qs('.price__regular .price-item', priceWrapper);
    var comparePriceEl = qs('[data-compare-price]', priceWrapper) || qs('.price__sale .price-item--regular', priceWrapper);
    var salePriceEl = qs('[data-sale-price]', priceWrapper) || qs('.price__sale .price-item--sale', priceWrapper);
    var priceContainer = qs('.price', priceWrapper);

    if (!variant) return;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      // On sale
      if (priceContainer) priceContainer.classList.add('price--on-sale');
      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
      }
      if (salePriceEl) {
        salePriceEl.textContent = formatMoney(variant.price);
        salePriceEl.style.display = '';
      }
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }
    } else {
      // Regular price
      if (priceContainer) priceContainer.classList.remove('price--on-sale');
      if (comparePriceEl) comparePriceEl.style.display = 'none';
      if (salePriceEl) salePriceEl.style.display = 'none';
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
    }
  }

  function updateAddToCartButton(variant) {
    if (!addToCartBtn || !addToCartText) return;

    if (!variant) {
      addToCartBtn.disabled = true;
      addToCartText.textContent = window.Charmify?.strings?.unavailable || 'לא זמין';
      return;
    }

    if (variant.available) {
      addToCartBtn.disabled = false;
      addToCartText.textContent = section.closest('[data-section-type]')
        ? (qs('[data-add-to-cart-text]', section).getAttribute('data-available-text') || 'הוסף לסל')
        : 'הוסף לסל';
    } else {
      addToCartBtn.disabled = true;
      addToCartText.textContent = 'אזל מהמלאי';
    }
  }

  function updateVariantInput(variant) {
    if (!variantIdInput || !variant) return;
    variantIdInput.value = variant.id;
  }

  /**
   * Update unavailable state on swatches / pills.
   * Cross-checks availability against all other selected options.
   */
  function updateOptionAvailability() {
    if (!productData || !productData.variants) return;

    var optionFieldsets = qsa('.product-main__option', section);
    var selectedOptions = getSelectedOptions();

    optionFieldsets.forEach(function (fieldset, optionIndex) {
      var inputs = qsa('input[type="radio"], select option', fieldset);

      inputs.forEach(function (input) {
        var value = input.value;

        // Build a hypothetical options array with this value at the current index
        var testOptions = selectedOptions.slice();
        testOptions[optionIndex] = value;

        // Check if any variant with these options is available
        var available = productData.variants.some(function (variant) {
          return testOptions.every(function (opt, idx) {
            return variant.options[idx] === opt;
          }) && variant.available;
        });

        if (input.tagName === 'INPUT') {
          var label = input.nextElementSibling || qs('label[for="' + input.id + '"]', fieldset);
          if (available) {
            input.disabled = false;
            if (label) {
              label.classList.remove('product-main__swatch--unavailable');
              label.classList.remove('product-main__pill--unavailable');
            }
          } else {
            input.disabled = true;
            if (label) {
              if (label.classList.contains('product-main__swatch')) {
                label.classList.add('product-main__swatch--unavailable');
              }
              if (label.classList.contains('product-main__pill')) {
                label.classList.add('product-main__pill--unavailable');
              }
            }
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Option value display (e.g., "Color — Gold")                         */
  /* ------------------------------------------------------------------ */

  function updateOptionValueDisplays() {
    var selectedOptions = getSelectedOptions();
    var displays = qsa('[data-option-value-display]', section);
    displays.forEach(function (el) {
      var index = parseInt(el.getAttribute('data-option-value-display'), 10);
      if (!isNaN(index) && selectedOptions[index]) {
        el.textContent = '\u2014 ' + selectedOptions[index]; // em-dash
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Central variant change handler                                      */
  /* ------------------------------------------------------------------ */

  function onVariantChange() {
    var selectedOptions = getSelectedOptions();
    var variant = findVariantFromOptions(selectedOptions);
    currentVariant = variant;

    updateVariantInput(variant);
    updatePriceDisplay(variant);
    updateAddToCartButton(variant);
    updateOptionAvailability();
    updateOptionValueDisplays();

    if (variant) {
      updateUrl(variant.id);

      // Dispatch event so gallery can switch to variant image
      document.dispatchEvent(new CustomEvent('charmify:variant:change', {
        detail: {
          variant: variant,
          featured_media_id: variant.featured_media_id || null
        }
      }));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bind variant option inputs                                          */
  /* ------------------------------------------------------------------ */

  function bindVariantInputs() {
    // Radios (swatches and pills)
    qsa('.product-main__swatch-input, .product-main__pill-input', section).forEach(function (input) {
      input.addEventListener('change', onVariantChange);
    });

    // Selects
    qsa('.product-main__select', section).forEach(function (select) {
      select.addEventListener('change', onVariantChange);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Description toggle                                                  */
  /* ------------------------------------------------------------------ */

  function bindDescriptionToggle() {
    var toggleBtn = qs('.product-main__description-toggle', section);
    if (!toggleBtn) return;

    var contentId = toggleBtn.getAttribute('aria-controls');
    var content = document.getElementById(contentId);
    if (!content) return;

    toggleBtn.addEventListener('click', function () {
      var isCollapsed = content.getAttribute('data-collapsed') === 'true';
      content.setAttribute('data-collapsed', isCollapsed ? 'false' : 'true');
      toggleBtn.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Engraving character counter                                         */
  /* ------------------------------------------------------------------ */

  function bindEngravingCounter() {
    var input = qs('.product-main__engraving-input', section);
    if (!input) return;

    var counter = qs('[data-char-count]', section);
    if (!counter) return;

    input.addEventListener('input', function () {
      counter.textContent = input.value.length;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Memory upload panel                                                 */
  /* ------------------------------------------------------------------ */

  function bindMemoryPanel() {
    var typeButtons = qsa('.product-main__memory-type', section);
    var panels = qsa('[data-memory-panel]', section);
    var contentTypeInput = qs('#MemoryContentType-' + sectionId, section);

    if (!typeButtons.length) return;

    typeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-content-type');

        // Toggle active type button
        typeButtons.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        // Toggle panels
        panels.forEach(function (panel) {
          var panelType = panel.getAttribute('data-memory-panel');
          if (panelType === type) {
            panel.classList.add('is-active');
          } else {
            panel.classList.remove('is-active');
          }
        });

        // Update hidden input
        if (contentTypeInput) {
          contentTypeInput.value = type;
        }
      });
    });

    // Memory text character counter
    var textInput = qs('.product-main__memory-text-input', section);
    var textCounter = qs('[data-text-char-count]', section);
    if (textInput && textCounter) {
      textInput.addEventListener('input', function () {
        textCounter.textContent = textInput.value.length;
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Form validation                                                     */
  /* ------------------------------------------------------------------ */

  function validateForm() {
    var errors = [];

    // Check variant is selected
    if (!currentVariant) {
      errors.push('נא לבחור את כל האפשרויות.');
    } else if (!currentVariant.available) {
      errors.push('הדגם הזה אזל מהמלאי.');
    }

    // Check memory link is valid if link type is active
    var contentType = qs('#MemoryContentType-' + sectionId);
    if (contentType && contentType.value === 'link') {
      var linkInput = qs('.product-main__memory-link-input', section);
      if (linkInput && linkInput.value.trim()) {
        try {
          new URL(linkInput.value.trim());
        } catch (_) {
          errors.push('נא להזין קישור תקין.');
        }
      }
    }

    return errors;
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart (AJAX)                                                  */
  /* ------------------------------------------------------------------ */

  function setLoadingState(loading) {
    if (!addToCartBtn) return;
    if (loading) {
      addToCartBtn.classList.add('is-loading');
      addToCartBtn.disabled = true;
    } else {
      addToCartBtn.classList.remove('is-loading');
      if (currentVariant && currentVariant.available) {
        addToCartBtn.disabled = false;
      }
    }
  }

  function collectLineItemProperties() {
    var properties = {};

    // Engraving
    var engravingInput = qs('.product-main__engraving-input', section);
    if (engravingInput && engravingInput.value.trim()) {
      properties['Engraving'] = engravingInput.value.trim();
    }

    // Memory content type
    var contentTypeInput = qs('#MemoryContentType-' + sectionId);
    if (contentTypeInput && contentTypeInput.value) {
      properties['_memory_content_type'] = contentTypeInput.value;
    }

    // Memory photo URL
    var photoUrl = qs('#MemoryPhotoUrl-' + sectionId);
    if (photoUrl && photoUrl.value) {
      properties['_memory_photo_url'] = photoUrl.value;
    }

    // Memory video URL
    var videoUrl = qs('#MemoryVideoUrl-' + sectionId);
    if (videoUrl && videoUrl.value) {
      properties['_memory_video_url'] = videoUrl.value;
    }

    // Memory link
    var linkInput = qs('.product-main__memory-link-input', section);
    if (linkInput && linkInput.value.trim()) {
      properties['_memory_link'] = linkInput.value.trim();
    }

    // Memory text
    var textInput = qs('.product-main__memory-text-input', section);
    if (textInput && textInput.value.trim()) {
      properties['_memory_text'] = textInput.value.trim();
    }

    // Gift lock
    var giftLockInput = qs('.product-main__gift-lock-input', section);
    if (giftLockInput && giftLockInput.checked) {
      properties['_gift_locked'] = 'true';
    }

    return properties;
  }

  function showToast(message, type) {
    // Use global CharmifyToast if available (from theme.js)
    if (window.Charmify && typeof window.Charmify.showToast === 'function') {
      window.Charmify.showToast(message, '', TOAST_DURATION);
      return;
    }

    // Fallback: create inline toast
    var existing = document.getElementById('charmify-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.id = 'charmify-toast';
    toast.className = 'charmify-toast charmify-toast--' + (type || 'success');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    toast.innerHTML =
      '<div class="charmify-toast__inner">' +
        (type === 'error'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>'
          : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
        ) +
        '<span>' + message + '</span>' +
      '</div>';

    document.body.appendChild(toast);

    // Force reflow for animation
    toast.offsetHeight; // eslint-disable-line no-unused-expressions
    toast.classList.add('is-visible');

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        toast.remove();
      }, 300);
    }, TOAST_DURATION);
  }

  function handleAddToCart(event) {
    event.preventDefault();

    var errors = validateForm();
    if (errors.length > 0) {
      showToast(errors[0], 'error');
      return;
    }

    setLoadingState(true);

    var properties = collectLineItemProperties();

    var payload = {
      id: currentVariant.id,
      quantity: 1,
      properties: properties
    };

    fetch(CART_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error(data.message || data.description || 'לא ניתן להוסיף לסל');
          });
        }
        return response.json();
      })
      .then(function () {
        setLoadingState(false);
        showToast('נוסף לסל!', 'success');

        // Dispatch events matching theme.js listeners
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: null } }));

        // Fetch updated cart and dispatch cart:updated with cart data
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
            var countEls = document.querySelectorAll('[data-cart-count]');
            countEls.forEach(function (el) {
              el.textContent = cart.item_count;
              if (cart.item_count > 0) {
                el.removeAttribute('hidden');
              }
            });
          })
          .catch(function () { /* silent */ });
      })
      .catch(function (error) {
        setLoadingState(false);
        showToast(error.message || 'לא ניתן להוסיף לסל. נסו שוב.', 'error');
        console.error('Charmify Add to Cart error:', error);
      });
  }

  function bindAddToCart() {
    if (!form) return;
    form.addEventListener('submit', handleAddToCart);
  }

  /* ------------------------------------------------------------------ */
  /* Memory upload button (opens a file picker or external modal)        */
  /* ------------------------------------------------------------------ */

  function bindMemoryUploadButtons() {
    var uploadBtns = qsa('[data-open-upload]', section);

    uploadBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-open-upload'); // 'photo' or 'video'
        var accept = type === 'photo' ? 'image/*' : 'video/*';

        // Create a transient file input
        var fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = accept;
        fileInput.style.display = 'none';

        fileInput.addEventListener('change', function () {
          if (!fileInput.files || !fileInput.files[0]) return;

          var file = fileInput.files[0];

          // Basic size check (10 MB for photo, 50 MB for video)
          var maxSize = type === 'photo' ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
          if (file.size > maxSize) {
            showToast('הקובץ גדול מדי — המקסימום ' + (type === 'photo' ? '10MB' : '50MB') + '.', 'error');
            return;
          }

          // Show file name feedback
          var uploadArea = btn.closest('[data-memory-panel]');
          if (uploadArea) {
            var hintEl = uploadArea.querySelector('.product-main__memory-upload-hint');
            if (hintEl) {
              hintEl.textContent = file.name;
            }
          }

          // Store as a local object URL in the hidden input.
          // In production, you would upload to your server / CDN and store the returned URL.
          var objectUrl = URL.createObjectURL(file);
          var hiddenInput = type === 'photo'
            ? qs('#MemoryPhotoUrl-' + sectionId)
            : qs('#MemoryVideoUrl-' + sectionId);

          if (hiddenInput) {
            hiddenInput.value = objectUrl;
          }

          // Store the File object for later form submission if needed
          if (!window.__charmifyUploads) window.__charmifyUploads = {};
          window.__charmifyUploads[type] = file;

          // Clean up
          fileInput.remove();
        });

        document.body.appendChild(fileInput);
        fileInput.click();
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    section = document.querySelector('.product-main');
    if (!section) return;

    sectionId = section.getAttribute('data-section-id');

    // Parse product JSON
    var jsonEl = document.getElementById('ProductJson-' + sectionId);
    if (jsonEl) {
      try {
        productData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('Charmify: Could not parse product JSON', e);
        return;
      }
    }

    form = qs('#ProductForm', section);
    variantIdInput = qs('#ProductVariantId-' + sectionId, section);
    addToCartBtn = qs('#AddToCart-' + sectionId, section);
    addToCartText = qs('[data-add-to-cart-text]', section);
    priceWrapper = qs('#ProductPrice-' + sectionId, section);

    // Set initial variant
    if (productData && productData.variants) {
      var urlParams = new URLSearchParams(window.location.search);
      var urlVariantId = urlParams.get('variant');
      if (urlVariantId) {
        currentVariant = productData.variants.find(function (v) {
          return String(v.id) === urlVariantId;
        }) || productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
      } else {
        currentVariant = productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
      }
    }

    bindVariantInputs();
    bindDescriptionToggle();
    bindEngravingCounter();
    bindMemoryPanel();
    bindMemoryUploadButtons();
    bindAddToCart();
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify section rendering API
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.product-main')) {
      init();
    }
  });
})();
